if (!customElements.get('article-toc')) {
  class ArticleToc extends HTMLElement {
    connectedCallback() {
      this.content = document.querySelector('[data-article-content]');
      this.navs = Array.from(document.querySelectorAll('[data-article-toc]'));
      if (!this.content || !this.navs.length) {
        this.hidden = true;
        return;
      }

      const headings = Array.from(this.content.querySelectorAll('h2'));
      if (headings.length < 2) {
        this.navs.forEach((nav) => {
          const box = nav.closest('.article-toc');
          if (box) box.hidden = true;
        });
        return;
      }

      this.onNavClick = this.onNavClick.bind(this);
      this.navs.forEach((nav) => nav.addEventListener('click', this.onNavClick));

      this.build(headings);
      this.observe(headings);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.navs.forEach((nav) => nav.removeEventListener('click', this.onNavClick));
    }

    onNavClick(evt) {
      const link = evt.target.closest('.article-toc__link');
      if (!link) return;

      const target = document.getElementById(link.getAttribute('href').slice(1));
      if (!target) return;

      evt.preventDefault();
      evt.stopPropagation();

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      this.setActive(target.id);
    }

    build(headings) {
      const items = headings.map((heading, index) => {
        if (!heading.id) heading.id = ArticleToc.slugify(heading.textContent) || 'section-' + index;
        return { id: heading.id, text: (heading.textContent || '').trim() };
      });

      this.navs.forEach((nav) => {
        const list = document.createElement('ul');
        list.className = 'article-toc__list';

        items.forEach((item) => {
          const link = document.createElement('a');
          link.className = 'article-toc__link';
          link.href = '#' + item.id;
          link.textContent = item.text;

          const li = document.createElement('li');
          li.appendChild(link);
          list.appendChild(li);
        });

        nav.replaceChildren(list);
        const box = nav.closest('.article-toc');
        if (box) box.hidden = false;
      });

      this.links = Array.from(document.querySelectorAll('[data-article-toc] .article-toc__link'));
    }

    observe(headings) {
      if (!('IntersectionObserver' in window)) return;

      const landing = parseInt(getComputedStyle(headings[0]).scrollMarginTop, 10) || 0;
      const bandTop = Math.max(landing - 16, 0);

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setActive(entry.target.id);
          });
        },
        { rootMargin: '-' + bandTop + 'px 0px -60% 0px', threshold: 0 }
      );

      headings.forEach((heading) => this.observer.observe(heading));
    }

    setActive(id) {
      this.links.forEach((link) => {
        link.classList.toggle('is-active', link.getAttribute('href') === '#' + id);
      });
    }

    static slugify(text) {
      return (text || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .slice(0, 60);
    }
  }

  customElements.define('article-toc', ArticleToc);
}

if (!customElements.get('article-body')) {
  class ArticleBody extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('table').forEach((table) => {
        const parent = table.parentElement;
        if (parent && parent.classList.contains('article-page__scroller')) return;

        const scroller = document.createElement('div');
        scroller.className = 'article-page__scroller';
        scroller.tabIndex = 0;
        table.replaceWith(scroller);
        scroller.appendChild(table);
      });
    }
  }

  customElements.define('article-body', ArticleBody);
}

if (!customElements.get('sticky-column')) {
  class StickyColumn extends HTMLElement {
    connectedCallback() {
      this.gap = parseInt(this.dataset.gap, 10) || 20;
      this.offset = null;
      this.lastY = window.scrollY;
      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);
      this.sync = this.sync.bind(this);

      this.mq = window.matchMedia(this.dataset.media || '(min-width: 1000px)');
      this.mq.addEventListener('change', this.sync);

      this.resizeObserver = new ResizeObserver(() => this.update(true));
      this.resizeObserver.observe(this);
      if (this.parentElement) this.resizeObserver.observe(this.parentElement);

      this.sync();
    }

    disconnectedCallback() {
      this.mq.removeEventListener('change', this.sync);
      this.resizeObserver.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }

    sync() {
      if (this.mq.matches) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.update(true);
        return;
      }

      window.removeEventListener('scroll', this.onScroll);
      this.offset = null;
      this.style.top = '';
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;

      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update(reset) {
      if (!this.mq.matches) {
        if (this.offset === null) return;
        this.offset = null;
        this.style.top = '';
        return;
      }

      const headerHeight = window.theme && typeof window.theme.stickyHeaderHeight === 'function'
        ? window.theme.stickyHeaderHeight()
        : 0;
      const maxOffset = headerHeight + this.gap;
      const minOffset = window.innerHeight - this.offsetHeight - this.gap;
      const scrollY = window.scrollY;
      const travelled = reset ? 0 : scrollY - this.lastY;

      this.lastY = scrollY;

      let next = maxOffset;
      if (minOffset < maxOffset) {
        const from = this.offset === null ? maxOffset : this.offset;
        next = Math.min(maxOffset, Math.max(minOffset, from - travelled));
      }

      if (next === this.offset) return;

      this.offset = next;
      this.style.top = `${next}px`;
    }
  }

  customElements.define('sticky-column', StickyColumn);
}
